import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AiRewriteModel,
  DashboardStyleScope,
  DashboardStyleSettingsService,
  DashboardStyleState,
} from '../../services/dashboard-style-settings.service';

@Component({
  selector: 'app-dashboard-style-settings-modal',
  templateUrl: './dashboard-style-settings.component.html',
  styleUrls: ['./dashboard-style-settings.component.scss']
})
export class DashboardStyleSettingsModalComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  private readonly maxBackgroundFileBytes = 10_000_000;

  activeTab: DashboardStyleScope | 'ai' = 'tasks';

  tasksState: DashboardStyleState;
  dmsState: DashboardStyleState;

  constructor(
    public activeModal: NgbActiveModal,
    private dashboardStyleSettingsService: DashboardStyleSettingsService,
    private translate: TranslateService,
  ) {
    this.tasksState = this.dashboardStyleSettingsService.getSnapshot('tasks');
    this.dmsState = this.dashboardStyleSettingsService.getSnapshot('dms');
  }

  get activeState(): DashboardStyleState {
    return this.activeTab === 'dms' ? this.dmsState : this.tasksState;
  }

  get aiRewriteModel(): AiRewriteModel {
    return this.dmsState.aiRewriteModel;
  }

  get aiRewritePromptPrefix(): string {
    return this.dmsState.aiRewritePromptPrefix;
  }

  get aiRewriteSystemContent(): string {
    return this.dmsState.aiRewriteSystemContent;
  }

  get aiRewriteTemperature(): number {
    return this.dmsState.aiRewriteTemperature;
  }

  private getActiveScope(): DashboardStyleScope {
    const activeTab = this.activeTab;
    if (activeTab === 'ai') {
      return 'dms';
    }

    return activeTab;
  }

  ngOnInit(): void {
    this.dashboardStyleSettingsService.watch('tasks')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.tasksState = state;
      });

    this.dashboardStyleSettingsService.watch('dms')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.dmsState = state;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updateActiveSettings(patch: Partial<Omit<DashboardStyleState, 'backgroundImageUrl'>>): void {
    void this.dashboardStyleSettingsService.updateSettings(this.getActiveScope(), patch);
  }

  updateAiRewriteModel(model: AiRewriteModel): void {
    void this.dashboardStyleSettingsService.updateSettings('dms', { aiRewriteModel: model });
  }

  updateAiRewriteTemperature(value: number): void {
    const temperature = Number(value);
    if (!Number.isFinite(temperature)) {
      return;
    }

    void this.dashboardStyleSettingsService.updateSettings('dms', {
      aiRewriteTemperature: Math.min(Math.max(temperature, 0), 1.4),
    });
  }

  onBackgroundFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type || !file.type.startsWith('image/')) {
      window.alert(this.translate.instant('PLEASE_SELECT_AN_IMAGE_FILE'));
      return;
    }

    if (file.size > this.maxBackgroundFileBytes) {
      window.alert(this.translate.instant('BACKGROUND_IMAGE_TOO_LARGE'));
      return;
    }

    void this.dashboardStyleSettingsService
      .setBackgroundFile(this.getActiveScope(), file)
      .catch(() => window.alert(this.translate.instant('UNABLE_TO_SAVE_BACKGROUND_IMAGE')));
  }

  removeBackgroundImage(): void {
    void this.dashboardStyleSettingsService.removeBackgroundImage(this.getActiveScope());
  }

  resetStyleSettingsToDefault(): void {
    if (this.activeTab === 'ai') {
      this.updateAiRewriteModel('gpt-5');
      return;
    }

    void this.dashboardStyleSettingsService.reset(this.getActiveScope());
  }
}
